import { MAX_PLAYERS, MIN_PLAYERS, QUESTION_PERIOD_CHOICES, type GameView } from '@secretnenshi/shared';
import { ReclaimRequests } from '../components/chrome';
import { Button, Narrator, Page, Section } from '../components/ui';
import { kickPlayer, leaveGame, play, startGame } from '../state/actions';
import { useStore } from '../state/store';
import { useTheme } from '../theme/useTheme';

export function Lobby({ view }: { view: GameView }) {
  const me = useStore((s) => s.session?.playerId);
  const busy = useStore((s) => s.busy);
  const isHost = view.hostPlayerId === me;
  const th = useTheme();
  const count = view.players.length;
  const canStart = count >= MIN_PLAYERS && count <= MAX_PLAYERS;
  // Tolerate a server that predates settings during a deploy.
  const qpSeconds = view.settings?.questionPeriodSeconds ?? 180;

  return (
    <Page>
      <div className="game-code">
        <span className="field-label">Game</span>
        <span className="code" aria-label={`Game code ${view.code.split('').join(' ')}`}>
          {view.code}
        </span>
      </div>

      {isHost ? <ReclaimRequests /> : null}

      <Section label={`${count} / ${MAX_PLAYERS}`}>
        <ol className="roster">
          {view.players.map((p) => (
            <li key={p.id} className="roster-row">
              <span className={`presence ${p.connected ? 'on' : 'off'}`} aria-label={p.connected ? 'online' : 'offline'} />
              <span className="roster-name">
                {p.name}
                {p.isHost ? <span className="tag"> — Host</span> : null}
                {p.id === me ? <span className="tag"> (you)</span> : null}
              </span>
              {isHost && p.id !== me ? (
                <button type="button" className="link small" disabled={busy} onClick={() => void kickPlayer(p.id)}>
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ol>
      </Section>

      <Section label="Question Period">
        {isHost ? (
          <div className="segmented" role="radiogroup" aria-label="Question Period length">
            {QUESTION_PERIOD_CHOICES.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={qpSeconds === s}
                className={`segment ${qpSeconds === s ? 'selected' : ''}`}
                disabled={busy}
                onClick={() => void play.setQuestionPeriod(s)}
              >
                {s / 60} min
              </button>
            ))}
          </div>
        ) : null}
        <p className="hint-left">
          Up to {qpSeconds / 60} {qpSeconds === 60 ? 'minute' : 'minutes'} of debate at the end
          of each round{isHost ? '' : ', set by the host'}.
        </p>
      </Section>

      <Narrator>
        {count >= 2 ? th.gathered(count) : th.t.lobby.quorum}
      </Narrator>

      <div className="stack">
        {isHost ? (
          <>
            <Button disabled={!canStart || busy} onClick={() => void startGame()}>
              Start game
            </Button>
            {!canStart ? <p className="hint">Needs {MIN_PLAYERS}–{MAX_PLAYERS} players.</p> : null}
          </>
        ) : (
          <p className="hint">Waiting for the host to start the game.</p>
        )}
        <Button variant="quiet" disabled={busy} onClick={() => void leaveGame()}>
          Leave game
        </Button>
      </div>
    </Page>
  );
}
